// src/services/themeService.ts
// ✅ IMPROVED: Darkened light mode colors for better visibility on white backgrounds
// 🎨 ENHANCED: Secondary colors now have proper contrast

import { supabase } from './supabase';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ThemePreset =
  | 'protanopia'
  | 'deuteranopia'
  | 'tritanopia'
  | 'default'
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'royal'
  | 'pink'
  | 'seaglass'
  | 'lavender'
  | 'mint'
  | 'gemstones'
  | 'pride'
  | 'trans'
  | 'bi'
  | 'pan'
  | 'lesbian'
  | 'gay'
  | 'ace'
  | 'nonbinary'
  | 'christmas'
  | 'halloween'
  | 'easter'
  | 'valentines'
  | 'midsummer';

// Free themes: accessibility themes + default only
export const FREE_THEMES: ThemePreset[] = ['default', 'protanopia', 'deuteranopia', 'tritanopia'];

// Helper to check if a theme requires premium
export const isThemePremium = (theme: ThemePreset): boolean => {
  return !FREE_THEMES.includes(theme);
};

export interface ThemeColors {
  light: {
    primary: string;
    primaryContrast?: string;
    secondary: string;
    secondaryContrast?: string;
    tertiary: string;
    tertiaryContrast?: string;
    // Rainbow theme uses 6 colors
    quaternary?: string;
    quaternaryContrast?: string;
    quinary?: string;
    quinaryContrast?: string;
    senary?: string;
    senaryContrast?: string;
  };
  dark: {
    primary: string;
    primaryContrast?: string;
    secondary: string;
    secondaryContrast?: string;
    tertiary: string;
    tertiaryContrast?: string;
    // Rainbow theme uses 6 colors
    quaternary?: string;
    quaternaryContrast?: string;
    quinary?: string;
    quinaryContrast?: string;
    senary?: string;
    senaryContrast?: string;
  };
}

export const THEME_PRESETS: Record<ThemePreset, { name: string; colors: ThemeColors }> = {
  protanopia: {
    name: 'Protanopia Safe',
    colors: {
      light: {
        primary: '#0284c7',    // Darker from #7dd3fc
        primaryContrast: '#ffffff',
        secondary: '#ca8a04',  // Darker from #fef08a
        secondaryContrast: '#000000',
        tertiary: '#9333ea',   // Darker from #d8b4fe
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#0369a1',
        primaryContrast: '#ffffff',
        secondary: '#ca8a04',
        secondaryContrast: '#000000',
        tertiary: '#7c3aed',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  deuteranopia: {
    name: 'Deuteranopia Safe',
    colors: {
      light: {
        primary: '#0891b2',    // Darker from #67e8f9
        primaryContrast: '#ffffff',
        secondary: '#d97706',  // Darker from #fde68a
        secondaryContrast: '#000000',
        tertiary: '#7c3aed',   // Darker from #c4b5fd
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#0e7490',
        primaryContrast: '#ffffff',
        secondary: '#d97706',
        secondaryContrast: '#000000',
        tertiary: '#6d28d9',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  tritanopia: {
    name: 'Tritanopia Safe',
    colors: {
      light: {
        primary: '#db2777',    // Darker from #f9a8d4
        primaryContrast: '#ffffff',
        secondary: '#0d9488',  // Darker from #5eead4
        secondaryContrast: '#ffffff',
        tertiary: '#dc2626',   // Darker from #fca5a5
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#db2777',
        primaryContrast: '#ffffff',
        secondary: '#0f766e',
        secondaryContrast: '#ffffff',
        tertiary: '#b91c1c',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  default: {
    name: 'Dice Cat Classic',
    colors: {
      light: {
        primary: '#16a34a',    // Darker from #4ade80
        primaryContrast: '#ffffff',
        secondary: '#ea580c',  // Darker from #fdba74
        secondaryContrast: '#ffffff',
        tertiary: '#9333ea',   // Darker from #d8b4fe
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#15803d',
        primaryContrast: '#ffffff',
        secondary: '#ea580c',
        secondaryContrast: '#ffffff',
        tertiary: '#9333ea',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  ocean: {
    name: 'Ocean Blue',
    colors: {
      light: {
        primary: '#0284c7',    // Darker from #38bdf8
        primaryContrast: '#ffffff',
        secondary: '#0891b2',  // Darker from #67e8f9
        secondaryContrast: '#ffffff',
        tertiary: '#2563eb',   // Darker from #93c5fd
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#0369a1',
        primaryContrast: '#ffffff',
        secondary: '#0891b2',
        secondaryContrast: '#ffffff',
        tertiary: '#2563eb',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  forest: {
    name: 'Forest Green',
    colors: {
      light: {
        primary: '#059669',    // Darker from #34d399
        primaryContrast: '#ffffff',
        secondary: '#0d9488',  // Darker from #6ee7b7
        secondaryContrast: '#ffffff',
        tertiary: '#0f766e',   // Darker from #5eead4
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#047857',
        primaryContrast: '#ffffff',
        secondary: '#059669',
        secondaryContrast: '#ffffff',
        tertiary: '#0f766e',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  sunset: {
    name: 'Sunset',
    colors: {
      light: {
        primary: '#dc2626',    // Darker from #f87171
        primaryContrast: '#ffffff',
        secondary: '#ca8a04',  // Darker from #fde047
        secondaryContrast: '#000000',
        tertiary: '#ea580c',   // Darker from #fdba74
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#b91c1c',
        primaryContrast: '#ffffff',
        secondary: '#d97706',
        secondaryContrast: '#000000',
        tertiary: '#c2410c',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  red: {
    name: 'Ruby Red',
    colors: {
      light: {
        primary: '#dc2626',    // Darker from #f87171
        primaryContrast: '#ffffff',
        secondary: '#ef4444',  // Darker from #fca5a5
        secondaryContrast: '#ffffff',
        tertiary: '#f87171',   // Darker from #fecaca
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#b91c1c',
        primaryContrast: '#ffffff',
        secondary: '#dc2626',
        secondaryContrast: '#ffffff',
        tertiary: '#ef4444',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  orange: {
    name: 'Tangerine',
    colors: {
      light: {
        primary: '#ea580c',    // Darker from #fb923c
        primaryContrast: '#ffffff',
        secondary: '#f97316',  // Darker from #fdba74
        secondaryContrast: '#000000',
        tertiary: '#fb923c',   // Darker from #fed7aa
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#c2410c',
        primaryContrast: '#ffffff',
        secondary: '#ea580c',
        secondaryContrast: '#ffffff',
        tertiary: '#f97316',
        tertiaryContrast: '#000000',
      },
    },
  },
  yellow: {
    name: 'Sunshine',
    colors: {
      light: {
        primary: '#ca8a04',    // Darker from #facc15
        primaryContrast: '#000000',
        secondary: '#eab308',  // Darker from #fde047
        secondaryContrast: '#000000',
        tertiary: '#facc15',   // Darker from #fef08a
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#a16207',
        primaryContrast: '#ffffff',
        secondary: '#ca8a04',
        secondaryContrast: '#000000',
        tertiary: '#eab308',
        tertiaryContrast: '#000000',
      },
    },
  },
  royal: {
    name: 'Royal Purple',
    colors: {
      light: {
        primary: '#9333ea',    // Darker from #c084fc
        primaryContrast: '#ffffff',
        secondary: '#a855f7',  // Darker from #d8b4fe
        secondaryContrast: '#000000',
        tertiary: '#c084fc',   // Darker from #e9d5ff
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#6d28d9',
        primaryContrast: '#ffffff',
        secondary: '#9333ea',
        secondaryContrast: '#ffffff',
        tertiary: '#7c3aed',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  pink: {
    name: 'Bubblegum',
    colors: {
      light: {
        primary: '#ec4899',    // Darker from #f9a8d4
        primaryContrast: '#000000',
        secondary: '#f472b6',  // Darker from #fbcfe8
        secondaryContrast: '#000000',
        tertiary: '#f9a8d4',   // Darker from #fce7f3
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#db2777',
        primaryContrast: '#ffffff',
        secondary: '#ec4899',
        secondaryContrast: '#000000',
        tertiary: '#f472b6',
        tertiaryContrast: '#000000',
      },
    },
  },
  seaglass: {
    name: 'Sea Glass',
    colors: {
      light: {
        primary: '#0891b2',    // Darker from #22d3ee
        primaryContrast: '#ffffff',
        secondary: '#0d9488',  // Darker from #5eead4
        secondaryContrast: '#ffffff',
        tertiary: '#06b6d4',   // Darker from #67e8f9
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#0e7490',
        primaryContrast: '#ffffff',
        secondary: '#0f766e',
        secondaryContrast: '#ffffff',
        tertiary: '#0891b2',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  lavender: {
    name: 'Lavender Fields',
    colors: {
      light: {
        primary: '#9333ea',    // Darker from #c084fc
        primaryContrast: '#ffffff',
        secondary: '#a855f7',  // Darker from #d8b4fe
        secondaryContrast: '#000000',
        tertiary: '#c026d3',   // Darker from #f0abfc
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#7c3aed',
        primaryContrast: '#ffffff',
        secondary: '#9333ea',
        secondaryContrast: '#ffffff',
        tertiary: '#c026d3',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  mint: {
    name: 'Mint Chocolate',
    colors: {
      light: {
        primary: '#10b981',    // Darker from #6ee7b7
        primaryContrast: '#000000',
        secondary: '#14b8a6',  // Darker from #5eead4
        secondaryContrast: '#000000',
        tertiary: '#65a30d',   // Darker from #bef264
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#059669',
        primaryContrast: '#ffffff',
        secondary: '#0f766e',
        secondaryContrast: '#ffffff',
        tertiary: '#65a30d',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  gemstones: {
    name: 'Gemstones',
    colors: {
      light: {
        primary: '#dc2626',    // Darker from #f87171
        primaryContrast: '#ffffff',
        secondary: '#9333ea',  // Darker from #c084fc
        secondaryContrast: '#ffffff',
        tertiary: '#2563eb',   // Darker from #93c5fd
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#b91c1c',
        primaryContrast: '#ffffff',
        secondary: '#7c3aed',
        secondaryContrast: '#ffffff',
        tertiary: '#2563eb',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  pride: {
    name: 'Rainbow Pride',
    colors: {
      light: {
        primary: '#dc2626',    // Red
        primaryContrast: '#ffffff',
        secondary: '#ca8a04',  // Yellow
        secondaryContrast: '#000000',
        tertiary: '#9333ea',   // Purple
        tertiaryContrast: '#ffffff',
        quaternary: '#ea580c', // Orange
        quaternaryContrast: '#ffffff',
        quinary: '#16a34a',    // Green
        quinaryContrast: '#ffffff',
        senary: '#2563eb',     // Blue
        senaryContrast: '#ffffff',
      },
      dark: {
        primary: '#ef4444',    // Red
        primaryContrast: '#ffffff',
        secondary: '#eab308',  // Yellow
        secondaryContrast: '#000000',
        tertiary: '#a855f7',   // Purple
        tertiaryContrast: '#ffffff',
        quaternary: '#f97316', // Orange
        quaternaryContrast: '#ffffff',
        quinary: '#22c55e',    // Green
        quinaryContrast: '#000000',
        senary: '#3b82f6',     // Blue
        senaryContrast: '#ffffff',
      },
    },
  },
  trans: {
    name: 'Trans Pride',
    colors: {
      light: {
        primary: '#3b82f6',    // Darker from #93c5fd
        primaryContrast: '#ffffff',
        secondary: '#ec4899',  // Darker from #f9a8d4
        secondaryContrast: '#000000',
        tertiary: '#94a3b8',   // Darker from #f3f4f6
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#2563eb',
        primaryContrast: '#ffffff',
        secondary: '#db2777',
        secondaryContrast: '#ffffff',
        tertiary: '#f9fafb',
        tertiaryContrast: '#000000',
      },
    },
  },
  bi: {
    name: 'Bi Pride',
    colors: {
      light: {
        primary: '#d946ef',    // Darker from #f0abfc
        primaryContrast: '#000000',
        secondary: '#9333ea',  // Darker from #c084fc
        secondaryContrast: '#ffffff',
        tertiary: '#3b82f6',   // Darker from #93c5fd
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#c026d3',
        primaryContrast: '#ffffff',
        secondary: '#7c3aed',
        secondaryContrast: '#ffffff',
        tertiary: '#2563eb',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  pan: {
    name: 'Pan Pride',
    colors: {
      light: {
        primary: '#ec4899',    // Darker from #f9a8d4
        primaryContrast: '#000000',
        secondary: '#facc15',  // Darker from #fef08a
        secondaryContrast: '#000000',
        tertiary: '#3b82f6',   // Darker from #93c5fd
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#db2777',
        primaryContrast: '#ffffff',
        secondary: '#ca8a04',
        secondaryContrast: '#000000',
        tertiary: '#2563eb',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  lesbian: {
    name: 'Lesbian Pride',
    colors: {
      light: {
        primary: '#f97316',    // Darker from #fdba74
        primaryContrast: '#000000',
        secondary: '#ec4899',  // Darker from #f9a8d4
        secondaryContrast: '#000000',
        tertiary: '#94a3b8',   // Darker from #f3f4f6
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#c2410c',
        primaryContrast: '#ffffff',
        secondary: '#db2777',
        secondaryContrast: '#ffffff',
        tertiary: '#f9fafb',
        tertiaryContrast: '#000000',
      },
    },
  },
  gay: {
    name: 'Gay Pride',
    colors: {
      light: {
        primary: '#0891b2',    // Darker from #22d3ee
        primaryContrast: '#ffffff',
        secondary: '#10b981',  // Darker from #6ee7b7
        secondaryContrast: '#000000',
        tertiary: '#94a3b8',   // Darker from #f3f4f6
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#0e7490',
        primaryContrast: '#ffffff',
        secondary: '#059669',
        secondaryContrast: '#ffffff',
        tertiary: '#f9fafb',
        tertiaryContrast: '#000000',
      },
    },
  },
  ace: {
    name: 'Ace Pride',
    colors: {
      light: {
        primary: '#9333ea',    // Darker from #c084fc
        primaryContrast: '#ffffff',
        secondary: '#6b7280',  // Darker from #d1d5db
        secondaryContrast: '#ffffff',
        tertiary: '#94a3b8',   // Darker from #f9fafb
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#6d28d9',
        primaryContrast: '#ffffff',
        secondary: '#4b5563',
        secondaryContrast: '#ffffff',
        tertiary: '#0a0d15',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  nonbinary: {
    name: 'Non-Binary Pride',
    colors: {
      light: {
        primary: '#facc15',
        primaryContrast: '#000000',
        secondary: '#9333ea',  // DARK purple needs WHITE text
        secondaryContrast: '#ffffff',
        tertiary: '#94a3b8',
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#ca8a04',
        primaryContrast: '#000000',
        secondary: '#7c3aed',  // Purple needs WHITE text
        secondaryContrast: '#ffffff',
        tertiary: '#0a0d15',
        tertiaryContrast: '#ffffff',
      },
    },
  },
  christmas: {
    name: '🎄 Christmas',
    colors: {
      light: {
        primary: '#16a34a',    // Christmas green
        primaryContrast: '#ffffff',
        secondary: '#dc2626',  // Christmas red
        secondaryContrast: '#ffffff',
        tertiary: '#eab308',   // Gold
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#22c55e',
        primaryContrast: '#000000',
        secondary: '#ef4444',
        secondaryContrast: '#ffffff',
        tertiary: '#facc15',
        tertiaryContrast: '#000000',
      },
    },
  },
  halloween: {
    name: '🎃 Halloween',
    colors: {
      light: {
        primary: '#f97316',    // Light pumpkin orange
        primaryContrast: '#000000',
        secondary: '#a855f7',  // Light purple (swapped from tertiary)
        secondaryContrast: '#000000',
        tertiary: '#374151',   // Darker gray/black (swapped from secondary)
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#ea580c',    // Dark pumpkin orange
        primaryContrast: '#ffffff',
        secondary: '#9333ea',  // Dark purple (swapped from tertiary)
        secondaryContrast: '#ffffff',
        tertiary: '#0f172a',   // Very dark black (swapped from secondary)
        tertiaryContrast: '#ffffff',
      },
    },
  },
  easter: {
    name: '🐰 Easter',
    colors: {
      light: {
        primary: '#f472b6',    // Light pastel pink
        primaryContrast: '#000000',
        secondary: '#c084fc',  // Light pastel purple
        secondaryContrast: '#000000',
        tertiary: '#67e8f9',   // Light pastel blue
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#ec4899',    // Darker pink
        primaryContrast: '#000000',
        secondary: '#a855f7',  // Darker purple
        secondaryContrast: '#000000',
        tertiary: '#22d3ee',   // Darker blue
        tertiaryContrast: '#000000',
      },
    },
  },
  valentines: {
    name: '💖 Valentine\'s',
    colors: {
      light: {
        primary: '#f472b6',    // Light pink
        primaryContrast: '#000000',
        secondary: '#ec4899',  // Light hot pink
        secondaryContrast: '#000000',
        tertiary: '#ef4444',   // Light red
        tertiaryContrast: '#ffffff',
      },
      dark: {
        primary: '#ec4899',    // Dark hot pink
        primaryContrast: '#000000',
        secondary: '#db2777',  // Dark deep pink
        secondaryContrast: '#ffffff',
        tertiary: '#dc2626',   // Dark red
        tertiaryContrast: '#ffffff',
      },
    },
  },
  midsummer: {
    name: '🌻 Midsummer',
    colors: {
      light: {
        primary: '#facc15',    // Light sunflower yellow
        primaryContrast: '#000000',
        secondary: '#60a5fa',  // Light sky blue
        secondaryContrast: '#000000',
        tertiary: '#34d399',   // Light grass green
        tertiaryContrast: '#000000',
      },
      dark: {
        primary: '#eab308',    // Darker yellow
        primaryContrast: '#000000',
        secondary: '#3b82f6',  // Darker blue
        secondaryContrast: '#ffffff',
        tertiary: '#10b981',   // Darker green
        tertiaryContrast: '#000000',
      },
    },
  },
};

class ThemeService {
  private currentMode: ThemeMode = 'system';
  private currentPreset: ThemePreset = 'default';
  private mediaQuery: MediaQueryList | null = null;
  private mutationObserver: MutationObserver | null = null;
  private isInitialized = false;

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    // Load preferences from localStorage FIRST (synchronous, instant)
    const storedMode = localStorage.getItem('theme-mode') as ThemeMode | null;
    const storedPreset = localStorage.getItem('theme-preset') as ThemePreset | null;

    this.currentMode = storedMode || 'system';
    this.currentPreset = storedPreset || 'default';

    // Apply theme IMMEDIATELY before anything else
    this.applyTheme();

    // Mark as initialized
    this.isInitialized = true;

    // Setup watchers
    this.watchSystemTheme();
    this.setupMutationObserver();

    // Load from database asynchronously (don't block initialization)
    // This will validate premium status and reset theme if needed
    this.loadUserPreferences();

    // Reapply theme on visibility/focus changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.applyTheme();
      }
    });

    window.addEventListener('focus', () => {
      this.applyTheme();
    });
  }

  private async loadUserPreferences() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('theme_mode, theme_preset, is_premium')
      .eq('id', user.id)
      .single();

    if (!error && data) {
      const dbMode = (data.theme_mode as ThemeMode) || 'system';
      let dbPreset = (data.theme_preset as ThemePreset) || 'default';
      const isPremium = data.is_premium || false;

      // SECURITY: If user is not premium and has a premium theme, reset to default
      if (!isPremium && isThemePremium(dbPreset)) {
        dbPreset = 'default';
        // Update database to remove premium theme
        await supabase
          .from('profiles')
          .update({ theme_preset: 'default' })
          .eq('id', user.id);
      }

      // Trust localStorage as source of truth if it differs from database
      // This prevents database from overwriting recent user changes
      const localMode = localStorage.getItem('theme-mode') as ThemeMode | null;
      let localPreset = localStorage.getItem('theme-preset') as ThemePreset | null;

      // SECURITY: Validate localStorage theme against premium status
      if (localPreset && !isPremium && isThemePremium(localPreset)) {
        localPreset = 'default';
        localStorage.setItem('theme-preset', 'default');
        this.currentPreset = 'default';
        this.applyTheme();
      }

      // If localStorage differs from database, save localStorage to database
      if ((localMode && localMode !== dbMode) || (localPreset && localPreset !== dbPreset)) {
        // localStorage is newer, save it to database
        await this.saveUserPreferences();
      } else if (dbMode !== this.currentMode || dbPreset !== this.currentPreset) {
        // Database has values not in localStorage, sync them
        this.currentMode = dbMode;
        this.currentPreset = dbPreset;

        // Sync to localStorage
        localStorage.setItem('theme-mode', this.currentMode);
        localStorage.setItem('theme-preset', this.currentPreset);

        // Reapply theme with new preferences
        this.applyTheme();
      }
    }
  }

  private async saveUserPreferences() {
    // Save to localStorage immediately
    localStorage.setItem('theme-mode', this.currentMode);
    localStorage.setItem('theme-preset', this.currentPreset);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return;
    }

    const updateData = {
      theme_mode: this.currentMode,
      theme_preset: this.currentPreset,
    };

    await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', user.id)
      .select();
  }

  async setThemeMode(mode: ThemeMode) {
    this.currentMode = mode;
    this.applyTheme();
    await this.saveUserPreferences();

    // Dispatch custom event to force re-render of components
    window.dispatchEvent(new CustomEvent('theme-mode-changed', { detail: { mode } }));
  }

  getThemeMode(): ThemeMode {
    return this.currentMode;
  }

  async setThemePreset(preset: ThemePreset, options: { skipEvent?: boolean; skipSave?: boolean } = {}) {
    this.currentPreset = preset;
    this.applyTheme();

    // Save to database unless explicitly skipped
    if (!options.skipSave) {
      await this.saveUserPreferences();
    } else {
      // Still save to localStorage for preview
      localStorage.setItem('theme-preset', this.currentPreset);
    }

    // Dispatch event unless explicitly skipped
    if (!options.skipEvent) {
      window.dispatchEvent(new CustomEvent('theme-preset-changed', { detail: { preset } }));
    }
  }

  getThemePreset(): ThemePreset {
    return this.currentPreset;
  }

  private isDarkMode(): boolean {
    if (this.currentMode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return this.currentMode === 'dark';
  }

  private applyTheme() {
    const isDark = this.isDarkMode();
    const preset = THEME_PRESETS[this.currentPreset];

    // Apply dark class FIRST, synchronously
    if (isDark) {
      document.body.classList.add('dark');
      document.documentElement.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
      document.documentElement.classList.remove('dark');
    }

    // Add rainbow-theme class for Pride theme
    if (this.currentPreset === 'pride') {
      document.body.classList.add('rainbow-theme');
      document.documentElement.classList.add('rainbow-theme');
      // Force rainbow theme styles inline to bypass any CSS loading issues
      this.applyRainbowThemeInline();
    } else {
      document.body.classList.remove('rainbow-theme');
      document.documentElement.classList.remove('rainbow-theme');
    }

    // Dark mode uses DARK colors, Light mode uses LIGHT colors
    const colors = isDark ? preset.colors.dark : preset.colors.light;

    // Apply CSS variables
    const root = document.documentElement;

    root.style.setProperty('--ion-color-primary', colors.primary);
    root.style.setProperty('--ion-color-primary-rgb', this.hexToRgb(colors.primary));
    root.style.setProperty('--ion-color-primary-contrast', colors.primaryContrast || (isDark ? '#000000' : '#ffffff'));
    root.style.setProperty('--ion-color-primary-contrast-rgb', this.hexToRgb(colors.primaryContrast || (isDark ? '#000000' : '#ffffff')));
    root.style.setProperty('--ion-color-primary-shade', this.adjustColor(colors.primary, -10));
    root.style.setProperty('--ion-color-primary-tint', this.adjustColor(colors.primary, 10));

    root.style.setProperty('--ion-color-secondary', colors.secondary);
    root.style.setProperty('--ion-color-secondary-rgb', this.hexToRgb(colors.secondary));
    root.style.setProperty('--ion-color-secondary-contrast', colors.secondaryContrast || (isDark ? '#000000' : '#ffffff'));
    root.style.setProperty('--ion-color-secondary-contrast-rgb', this.hexToRgb(colors.secondaryContrast || (isDark ? '#000000' : '#ffffff')));
    root.style.setProperty('--ion-color-secondary-shade', this.adjustColor(colors.secondary, -10));
    root.style.setProperty('--ion-color-secondary-tint', this.adjustColor(colors.secondary, 10));

    root.style.setProperty('--ion-color-tertiary', colors.tertiary);
    root.style.setProperty('--ion-color-tertiary-rgb', this.hexToRgb(colors.tertiary));
    root.style.setProperty('--ion-color-tertiary-contrast', colors.tertiaryContrast || (isDark ? '#000000' : '#ffffff'));
    root.style.setProperty('--ion-color-tertiary-contrast-rgb', this.hexToRgb(colors.tertiaryContrast || (isDark ? '#000000' : '#ffffff')));
    root.style.setProperty('--ion-color-tertiary-shade', this.adjustColor(colors.tertiary, -10));
    root.style.setProperty('--ion-color-tertiary-tint', this.adjustColor(colors.tertiary, 10));

    // Rainbow theme uses 6 colors (quaternary, quinary, senary)
    if (colors.quaternary) {
      root.style.setProperty('--ion-color-quaternary', colors.quaternary);
      root.style.setProperty('--ion-color-quaternary-rgb', this.hexToRgb(colors.quaternary));
      root.style.setProperty('--ion-color-quaternary-contrast', colors.quaternaryContrast || (isDark ? '#000000' : '#ffffff'));
      root.style.setProperty('--ion-color-quaternary-contrast-rgb', this.hexToRgb(colors.quaternaryContrast || (isDark ? '#000000' : '#ffffff')));
      root.style.setProperty('--ion-color-quaternary-shade', this.adjustColor(colors.quaternary, -10));
      root.style.setProperty('--ion-color-quaternary-tint', this.adjustColor(colors.quaternary, 10));
    }

    if (colors.quinary) {
      root.style.setProperty('--ion-color-quinary', colors.quinary);
      root.style.setProperty('--ion-color-quinary-rgb', this.hexToRgb(colors.quinary));
      root.style.setProperty('--ion-color-quinary-contrast', colors.quinaryContrast || (isDark ? '#000000' : '#ffffff'));
      root.style.setProperty('--ion-color-quinary-contrast-rgb', this.hexToRgb(colors.quinaryContrast || (isDark ? '#000000' : '#ffffff')));
      root.style.setProperty('--ion-color-quinary-shade', this.adjustColor(colors.quinary, -10));
      root.style.setProperty('--ion-color-quinary-tint', this.adjustColor(colors.quinary, 10));
    }

    if (colors.senary) {
      root.style.setProperty('--ion-color-senary', colors.senary);
      root.style.setProperty('--ion-color-senary-rgb', this.hexToRgb(colors.senary));
      root.style.setProperty('--ion-color-senary-contrast', colors.senaryContrast || (isDark ? '#000000' : '#ffffff'));
      root.style.setProperty('--ion-color-senary-contrast-rgb', this.hexToRgb(colors.senaryContrast || (isDark ? '#000000' : '#ffffff')));
      root.style.setProperty('--ion-color-senary-shade', this.adjustColor(colors.senary, -10));
      root.style.setProperty('--ion-color-senary-tint', this.adjustColor(colors.senary, 10));
    }

    // Apply background colors
    if (isDark) {
      root.style.setProperty('--ion-background-color', '#111827');
      root.style.setProperty('--ion-background-color-rgb', '17, 24, 39');
      root.style.setProperty('--ion-text-color', '#f9fafb');
      root.style.setProperty('--ion-text-color-rgb', '249, 250, 251');
      root.style.setProperty('--ion-border-color', '#374151');
      root.style.setProperty('--ion-card-background', '#1f2937');
      root.style.setProperty('--ion-toolbar-background', '#1f2937');
      root.style.setProperty('--ion-toolbar-color', '#f9fafb');
      root.style.setProperty('--ion-tab-bar-background', '#1f2937');
      root.style.setProperty('--ion-item-background', '#1f2937');
    } else {
      root.style.setProperty('--ion-background-color', '#ffffff');
      root.style.setProperty('--ion-background-color-rgb', '255, 255, 255');
      root.style.setProperty('--ion-text-color', '#1f2937');
      root.style.setProperty('--ion-text-color-rgb', '31, 41, 55');
      root.style.setProperty('--ion-border-color', '#e5e7eb');
      root.style.setProperty('--ion-card-background', '#ffffff');
      root.style.setProperty('--ion-toolbar-background', colors.primary);
      root.style.setProperty('--ion-toolbar-color', '#000000'); // BLACK TEXT IN LIGHT MODE
      root.style.setProperty('--ion-tab-bar-background', '#ffffff');
      root.style.setProperty('--ion-item-background', '#ffffff');
    }

    // Force immediate repaint of all elements
    this.forceCompleteRepaint();

    // Update mutation observer
    this.updateMutationObserverState(isDark);
  }

  private forceCompleteRepaint() {
    // Force a more aggressive repaint by toggling a class
    document.body.classList.add('theme-transitioning');

    // Force reflow on ALL elements
    document.querySelectorAll('*').forEach(el => {
      const element = el as HTMLElement;
      void element.offsetHeight;
    });

    // Remove transition class after a frame
    requestAnimationFrame(() => {
      document.body.classList.remove('theme-transitioning');
    });

    // Force specific Ionic components to repaint
    document.querySelectorAll('ion-toolbar, ion-title, ion-header, ion-content, ion-list, ion-item, ion-label, ion-card, ion-select, ion-toggle, ion-button').forEach(el => {
      const element = el as HTMLElement;
      void element.offsetHeight;
    });
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0, 0, 0';
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `${r}, ${g}, ${b}`;
  }

  private adjustColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    ).toString(16).slice(1);
  }

  watchSystemTheme() {
    if (this.mediaQuery) {
      return; // Already watching
    }

    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (this.currentMode === 'system') {
        this.applyTheme();
      }
    };

    this.mediaQuery.addEventListener('change', handleChange);
  }

  forceRefresh() {
    this.applyTheme();

    // Force immediate styling of all existing elements
    setTimeout(() => {
      const isDark = this.isDarkMode();
      this.styleExistingElements(isDark);
    }, 0);
  }

  private styleExistingElements(isDark: boolean) {
    const darkBg = '#1f2937';
    const lightBg = '#ffffff';
    const darkText = '#f9fafb';
    const lightText = '#1f2937';

    // Style all existing toolbars
    document.querySelectorAll('ion-toolbar').forEach(toolbar => {
      if (isDark) {
        (toolbar as HTMLElement).style.setProperty('--background', darkBg);
        (toolbar as HTMLElement).style.setProperty('--color', darkText);
      } else {
        (toolbar as HTMLElement).style.setProperty('--background', 'var(--ion-color-primary)');
        (toolbar as HTMLElement).style.setProperty('--color', '#000000'); // BLACK TEXT IN LIGHT MODE
      }
    });

    // Style all existing titles
    document.querySelectorAll('ion-title').forEach(title => {
      if (isDark) {
        (title as HTMLElement).style.setProperty('--color', '#ffffff');
        (title as HTMLElement).style.color = '#ffffff';
      } else {
        (title as HTMLElement).style.setProperty('--color', '#000000'); // BLACK TEXT IN LIGHT MODE
        (title as HTMLElement).style.color = '#000000';
      }
    });

    // Style all existing items
    document.querySelectorAll('ion-item').forEach(item => {
      (item as HTMLElement).style.setProperty('--background', isDark ? darkBg : lightBg);
      (item as HTMLElement).style.setProperty('--color', isDark ? darkText : lightText);
    });

    // Style all existing cards
    document.querySelectorAll('ion-card').forEach(card => {
      const bg = isDark ? darkBg : lightBg;
      (card as HTMLElement).style.setProperty('--background', bg);
      (card as HTMLElement).style.backgroundColor = bg;
    });

    // Style all existing labels
    document.querySelectorAll('ion-label').forEach(label => {
      const txt = isDark ? darkText : lightText;
      (label as HTMLElement).style.setProperty('--color', txt);
      (label as HTMLElement).style.color = txt;
    });
  }

  private setupMutationObserver() {
    this.updateMutationObserverState(this.isDarkMode());
  }

  private applyRainbowThemeInline() {
    const isDark = this.isDarkMode();

    // Get actual color values from the theme preset
    const orangeColor = isDark ? '#f97316' : '#ea580c';  // quaternary
    const yellowColor = isDark ? '#eab308' : '#ca8a04';  // secondary
    const greenColor = isDark ? '#22c55e' : '#16a34a';   // quinary

    // Apply immediately and also after a delay to catch dynamically loaded elements
    const applyStyles = () => {
      // CHATS / connections - Orange
      const chatsButtons = document.querySelectorAll('ion-segment-button[value="connections"]');
      chatsButtons.forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', orangeColor, 'important');
      });

      // REQUESTS - Yellow
      const requestsButtons = document.querySelectorAll('ion-segment-button[value="requests"]');
      requestsButtons.forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', yellowColor, 'important');
      });

      // GROUPS - Green
      const groupsButtons = document.querySelectorAll('ion-segment-button[value="groups"]');
      groupsButtons.forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', greenColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', greenColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', greenColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', greenColor, 'important');
      });

      // SESSIONS - Soon/Past/All
      document.querySelectorAll('ion-segment-button[value="upcoming"]').forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', orangeColor, 'important');
      });

      document.querySelectorAll('ion-segment-button[value="past"]').forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', yellowColor, 'important');
      });

      document.querySelectorAll('ion-segment-button[value="all"]').forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', greenColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', greenColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', greenColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', greenColor, 'important');
      });

      // DISCOVER - Matches/Find Friends
      document.querySelectorAll('ion-segment-button[value="matches"]').forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', orangeColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', orangeColor, 'important');
      });

      document.querySelectorAll('ion-segment-button[value="search"]').forEach(btn => {
        (btn as HTMLElement).style.setProperty('--indicator-color', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--color-checked', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--color', yellowColor, 'important');
        (btn as HTMLElement).style.setProperty('--ripple-color', yellowColor, 'important');
      });

      // Remove orange border and background from menu buttons in header
      document.querySelectorAll('ion-header ion-toolbar ion-button').forEach(btn => {
        (btn as HTMLElement).style.setProperty('border', 'none', 'important');
        (btn as HTMLElement).style.setProperty('--border-width', '0', 'important');
        (btn as HTMLElement).style.setProperty('--box-shadow', 'none', 'important');
        (btn as HTMLElement).style.setProperty('--background', 'transparent', 'important');
        (btn as HTMLElement).style.setProperty('background', 'transparent', 'important');
        (btn as HTMLElement).style.setProperty('--background-hover', 'rgba(255, 255, 255, 0.1)', 'important');
        (btn as HTMLElement).style.setProperty('--background-activated', 'rgba(255, 255, 255, 0.2)', 'important');
      });

      // Ensure menu icons are white
      document.querySelectorAll('ion-header ion-toolbar ion-button ion-icon').forEach(icon => {
        (icon as HTMLElement).style.setProperty('color', '#ffffff', 'important');
      });
    };

    // Apply immediately
    applyStyles();

    // Apply after delays to catch late-loading elements
    setTimeout(applyStyles, 100);
    setTimeout(applyStyles, 500);
    setTimeout(applyStyles, 1000);

    // Apply on route changes too
    const observer = new MutationObserver(() => {
      if (this.currentPreset === 'pride') {
        applyStyles();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private updateMutationObserverState(isDark: boolean) {
    const darkBg = '#1f2937';
    const lightBg = '#ffffff';
    const darkText = '#f9fafb';
    const lightText = '#1f2937';

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const element = node as HTMLElement;

            // Apply theme-aware styling to dynamically added elements
            if (element.tagName === 'ION-ITEM') {
              element.style.setProperty('--background', isDark ? darkBg : lightBg);
              element.style.setProperty('--color', isDark ? darkText : lightText);
            }

            if (element.tagName === 'ION-CARD') {
              const bg = isDark ? darkBg : lightBg;
              element.style.setProperty('--background', bg);
              element.style.backgroundColor = bg;
            }

            if (element.tagName === 'ION-TAB-BAR') {
              const tabBarBg = isDark ? '#1f2937' : '#ffffff';
              element.style.setProperty('--background', tabBarBg);
              element.style.backgroundColor = tabBarBg;
            }

            // Query for nested elements
            const items = element.querySelectorAll('ion-item');
            items.forEach(item => {
              (item as HTMLElement).style.setProperty('--background', isDark ? darkBg : lightBg);
              (item as HTMLElement).style.setProperty('--color', isDark ? darkText : lightText);
            });

            const cards = element.querySelectorAll('ion-card');
            cards.forEach(card => {
              const bg = isDark ? darkBg : lightBg;
              (card as HTMLElement).style.setProperty('--background', bg);
              (card as HTMLElement).style.backgroundColor = bg;
            });
          }
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

export const themeService = new ThemeService();